import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../../components/Navbar';
import OnboardingOverlay from '../../components/OnboardingOverlay';
import CustomerOnboarding from '../../components/CustomerOnboarding';
import AccountSettings from '../../components/AccountSettings';
import ProfileSettings from '../../components/ProfileSettings';
import FavoritesList from '../../components/FavoritesList';
import EventNotes from '../../components/EventNotes';
import CustomerRequests from '../../components/CustomerRequests';
import usePageTitle from '../../hooks/usePageTitle';
import { Heart, Star, MapPin, Calendar, FileText, Settings, Loader } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDashboard = ({ user, onLogout, onUpdateUser }) => {
  const navigate = useNavigate();
  usePageTitle('Mein Konto');
  const [stats, setStats] = useState({ total_requests: 0, favorites_count: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const statsRes = await axios.get(`${API}/customer/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-[#2B2142]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF0D1]/30 via-white to-[#F4F2F2]">
      <OnboardingOverlay user={user} />
      <CustomerOnboarding user={user} />
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="pt-20 md:pt-24 pb-8 md:pb-12 px-4">
        <div className="container max-w-6xl mx-auto">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2">Willkommen zurück!</h1>
            <p className="text-sm md:text-base text-gray-600">Verwalte deine Anfragen und Favoriten</p>
          </div>

          {/* Stats Cards */}
          <div className="mb-6 md:mb-8">
            <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 max-w-sm">
              <div className="flex items-center gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Heart className="w-5 h-5 md:w-6 md:h-6 text-red-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs md:text-sm text-gray-600 truncate">Favoriten</p>
                  <p className="text-xl md:text-2xl font-bold text-gray-900">{stats.favorites_count}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="requests" className="w-full">
            <div className="bg-white rounded-2xl shadow-sm p-2 mb-6 md:mb-8">
              <TabsList className="w-full">
                <TabsTrigger value="requests" className="flex-1 sm:flex-none">Meine Anfragen</TabsTrigger>
                <TabsTrigger value="favorites" className="flex-1 sm:flex-none">Favoriten</TabsTrigger>
                <TabsTrigger value="notes" className="flex-1 sm:flex-none">Notizen</TabsTrigger>
                <TabsTrigger value="settings" className="flex-1 sm:flex-none">Einstellungen</TabsTrigger>
              </TabsList>
            </div>

            {/* Requests Tab */}
            <TabsContent value="requests">
              <CustomerRequests />
            </TabsContent>

            {/* Favorites Tab */}
            <TabsContent value="favorites">
              <FavoritesList />
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes">
              <EventNotes />
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings">
              <div className="space-y-6">
                <ProfileSettings user={user} onUpdateUser={onUpdateUser} />
                <AccountSettings user={user} onLogout={onLogout} />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
